import React, { useEffect, useState } from "react";
import { Select, DatePicker, Switch } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import { range } from "pdf-lib";

const { RangePicker } = DatePicker;

interface DateRangePickerProps {
  defaultRangeType?: string;
  rangeOptions: { label: string; value: string }[];
  onDateRangeChange: (
    dates: [dayjs.Dayjs, dayjs.Dayjs] | null,
    dateStrings: [string, string],
    range: any
  ) => void;
  className?: string;
  style?: React.CSSProperties;
  weekStart?: "monday" | "sunday";
  currentFromDate?: string | null;
  currentToDate?: string | null;
  currentRange?: string;
  defaultSingleWeekMode?: boolean;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  defaultRangeType = "week",
  rangeOptions,
  onDateRangeChange,
  className = "",
  style = {},
  weekStart = "monday",
  currentFromDate,
  currentToDate,
  currentRange,
  defaultSingleWeekMode = false,
}) => {
  
  const [rangeType, setRangeType] = useState(defaultRangeType);
  const [selectedDates, setSelectedDates] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | null
  >(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [singleWeekMode, setSingleWeekMode] = useState(defaultSingleWeekMode);

  const [referenceDates, setReferenceDates] = useState<{
    day?: dayjs.Dayjs;
    week?: [dayjs.Dayjs, dayjs.Dayjs];
    month?: dayjs.Dayjs;
    year?: dayjs.Dayjs;
  }>({});

  const getWeekBoundaries = (date: dayjs.Dayjs): [dayjs.Dayjs, dayjs.Dayjs] => {
    let start: dayjs.Dayjs;
    const day = date.day();
    if (weekStart === "monday") {
      const offset = day === 0 ? -6 : 1 - day;
      start = date.add(offset, "day").startOf("day");
    } else {
      start = date.subtract(day, "day").startOf("day");
    }
    const end = start.add(6, "day").endOf("day");
    return [start, end];
  };

  const getDefaultDatesForRangeType = (
    type: string,
    referenceDate?: dayjs.Dayjs
  ): [dayjs.Dayjs, dayjs.Dayjs] => {
    const baseDate = referenceDate || dayjs();

    switch (type) {
      case "Year":
        const yearRef = referenceDates.day || referenceDates.month || baseDate;
        return [yearRef.startOf("year"), yearRef.endOf("year")];

      case "Month":
        if (selectedDates?.[0] && selectedDates?.[1]) {
          return [
            selectedDates[0].startOf("month"),
            selectedDates[1].endOf("month"),
          ];
        }
        const monthRef = referenceDates.day || baseDate;
        return [monthRef.startOf("month"), monthRef.endOf("month")];

      case "Week":
        if (selectedDates?.[0] && selectedDates?.[1]) {
          const [startWeek] = getWeekBoundaries(selectedDates[0]);
          const [, endWeek] = getWeekBoundaries(selectedDates[1]);
          return [startWeek, endWeek];
        }
        const weekRef = referenceDates.day || baseDate;
        return getWeekBoundaries(weekRef);

      case "Day":
      default:
        return [baseDate.startOf("day"), baseDate.endOf("day")];
    }
  };

  const getFormatForRangeType = (type: string): string => {
    switch (type) {
      case "Year":
        return "YYYY";
      case "Month":
        return "YYYY-MM";
      case "Week":
        return "YYYY-MM-DD";
      default:
        return "YYYY-MM-DD hh:mm A";
    }
  };

  const updateReferenceDates = (
    type: string,
    startDate: dayjs.Dayjs,
    endDate: dayjs.Dayjs
  ) => {
    const newReferenceDates = { ...referenceDates };

    switch (type) {
      case "Day":
        newReferenceDates.day = startDate;
        break;
      case "Week":
        newReferenceDates.week = [startDate, endDate];
        if (!newReferenceDates.day || newReferenceDates.day.isBefore(startDate) || newReferenceDates.day.isAfter(endDate)) {
          newReferenceDates.day = startDate;
        }
        break;
      case "Month":
        newReferenceDates.month = startDate;
        if (!newReferenceDates.day || !newReferenceDates.day.isSame(startDate, "month")) {
          newReferenceDates.day = startDate;
        }
        break;
      case "Year":
        newReferenceDates.year = startDate;
        if (!newReferenceDates.month || !newReferenceDates.month.isSame(startDate, "year")) {
          newReferenceDates.month = startDate;
        }
        if (!newReferenceDates.day || !newReferenceDates.day.isSame(startDate, "year")) {
          newReferenceDates.day = startDate;
        }
        break;
    }

    setReferenceDates(newReferenceDates);
  };

  useEffect(() => {
    if (!isInitialized) {
      const initialRangeType = currentRange
        ? currentRange.charAt(0).toUpperCase() + currentRange.slice(1)
        : defaultRangeType;
      
      setRangeType(initialRangeType);

      if (currentFromDate && currentToDate && currentRange !== "total") {
        
        const startDate = dayjs(currentFromDate);
        const endDate = dayjs(currentToDate);
        const [start, end] = getDefaultDatesForRangeType(initialRangeType, startDate);
        setSelectedDates([start, end]);
        updateReferenceDates(initialRangeType, start, end);
      } else if (currentRange !== "total") {
        const [start, end] = getDefaultDatesForRangeType(initialRangeType);
        setSelectedDates([start, end]);
        updateReferenceDates(initialRangeType, start, end);

        const format = getFormatForRangeType(initialRangeType);
        onDateRangeChange([start, end], [start.format(format), end.format(format)], initialRangeType.toLowerCase());
      } else {
        const [start, end] = getDefaultDatesForRangeType("Week");
        const format = getFormatForRangeType("Week");
        setSelectedDates([start, end]);
        updateReferenceDates("Week", start, end);
        onDateRangeChange(
          [start, end],
          [start.format(format), end.format(format)],
          "week"
        );
      }

      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (!isInitialized || !currentRange) return;

    const newRangeType = currentRange.charAt(0).toUpperCase() + currentRange.slice(1);
    if (newRangeType !== rangeType) {
      setRangeType(newRangeType);
    }

    if (currentFromDate && currentToDate && currentRange !== "total") {
      const startDate = dayjs(currentFromDate);
      const endDate = dayjs(currentToDate);
      const [start, end] = getDefaultDatesForRangeType(newRangeType, startDate);
      if (!selectedDates || !selectedDates[0]?.isSame(start) || !selectedDates[1]?.isSame(end)) {
        setSelectedDates([start, end]);
        updateReferenceDates(newRangeType, start, end);
      }
    } else if (currentRange === "total" && selectedDates !== null) {
      setSelectedDates(null);
    }
  }, [currentFromDate, currentToDate, currentRange, isInitialized]);

  useEffect(() => {
    if (!isInitialized) return;

    if (rangeType === "total") {
      if (currentRange !== "total") {
        setSelectedDates(null);
        onDateRangeChange(null, ["", ""], "total");
      }
      return;
    }

    if (currentRange?.toLowerCase() === rangeType.toLowerCase()) {
      return;
    }

    const [start, end] = getDefaultDatesForRangeType(rangeType);
    const format = getFormatForRangeType(rangeType);

    setSelectedDates([start, end]);
    updateReferenceDates(rangeType, start, end);
    onDateRangeChange(
      [start, end],
      [start.format(format), end.format(format)],
      rangeType.toLowerCase()
    );
  }, [rangeType, isInitialized]);


  const handleRangeTypeChange = (value: string) => {
    setRangeType(value);
  };

  const handleDateChange = (
  dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
  dateStrings: [string, string]
) => {
  if (dates && dates[0] && dates[1]) {
    let [startTime, endTime] = dates;

    switch (rangeType) {
      case "Year":
        startTime = startTime.startOf("year");
        endTime = endTime.endOf("year");
        break;

      case "Month":
        // Normalize across multiple months if selected
        startTime = startTime.startOf("month");
        endTime = endTime.endOf("month");
        break;

      case "Week":
        // Normalize to Monday–Sunday ranges
        [startTime] = getWeekBoundaries(startTime);
        [, endTime] = getWeekBoundaries(endTime);
        break;

      default:
        startTime = startTime.startOf("day");
        endTime = endTime.endOf("day");
    }

    const format = getFormatForRangeType(rangeType);
    const startFormatted = startTime.format(format);
    const endFormatted = endTime.format(format);

    setSelectedDates([startTime, endTime]);
    updateReferenceDates(rangeType, startTime, endTime);
    onDateRangeChange([startTime, endTime], [startFormatted, endFormatted], rangeType.toLowerCase());
  } else {
    setSelectedDates(null);
    onDateRangeChange(null, dateStrings, rangeType.toLowerCase());
  }
};


  const handleWeekModeToggle = (checked: boolean) => {
    setSingleWeekMode(checked);
  };

  const handleSingleWeekChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      const [weekStart, weekEnd] = getWeekBoundaries(date);
      const format = getFormatForRangeType(rangeType);
      const startFormatted = weekStart.format(format);
      const endFormatted = weekEnd.format(format);

      setSelectedDates([weekStart, weekEnd]);
      updateReferenceDates(rangeType, weekStart, weekEnd);
      onDateRangeChange([weekStart, weekEnd], [startFormatted, endFormatted], rangeType.toLowerCase());
    } else {
      setSelectedDates(null);
      onDateRangeChange(null, ["", ""], rangeType.toLowerCase());
    }
  };

  return (
    <div
      style={{
        ...style,
        display: "flex",
        whiteSpace: "nowrap",
        alignItems: "center",
        gap: "10px",
        width: "30%",
        minWidth: rangeType !== "Total" ? "540px" : undefined,
      }}
    >
      <Select
        value={rangeType}
        options={rangeOptions}
        onChange={handleRangeTypeChange}
        className="range-select"
        style={style}
      />

      {rangeType === "Week" && (
        <div style={{ display: "flex", alignItems: "center", gap: "5px", whiteSpace: "nowrap" }}>
          <span style={{ fontSize: "12px", color: "#666" }}>Single Week:</span>
          <Switch
            size="small"
            checked={singleWeekMode}
            onChange={handleWeekModeToggle}
            style={{ minWidth: "28px" }}
          />
        </div>
      )}

      {rangeType !== "Total" && (
        <>
          {rangeType === "Week" && singleWeekMode ? (
            <DatePicker
              style={{ width: "420px", height: "39px" }}
              value={selectedDates ? selectedDates[0] : null}
              format="YYYY-wo"
              picker="week"
              placeholder="Select Week"
              onChange={handleSingleWeekChange}
              disabledDate={(current) =>
                current && current.valueOf() > moment().endOf("day").valueOf()
              }
              allowClear={false}
            />
          ) : (
            <RangePicker
              style={{ width: "420px", height: "39px" }}
              showTime={{
                use12Hours: true,
                format: "h:mm A",
              }}
              value={selectedDates}
              format={
                rangeType === "Year"
                  ? "YYYY"
                  : rangeType === "Month"
                  ? "YYYY-MM"
                  : rangeType === "Week"
                  ? "YYYY-wo"
                  : "YYYY-MM-DD hh:mm A"
              }
              picker={
                rangeType === "Year"
                  ? "year"
                  : rangeType === "Month"
                  ? "month"
                  : rangeType === "Week"
                  ? "week"
                  : "date"
              }
              placeholder={["From", "To"]}
              onChange={handleDateChange}
              disabledDate={(current) =>
                current && current.valueOf() > moment().endOf("day").valueOf()
              }
              allowClear={false}
            />
          )}
        </>
      )}
    </div>
  );
};

export default DateRangePicker;
